import InstantCard from "./InstantCard";
import TimeSelector from "./TimeSelector";
import { useSelector } from "react-redux";
import { selectUser } from "../../store/authSlice";
import { selectInfoData } from "../../store/informationSlice";
import { selectWsData } from "../../store/wsSlice";
import { useState, useEffect, useMemo } from "react";
import { selectEquipmentDetail } from "../../store/informationSlice";
import formatDateTime from "./FormatDateTime";

export default function InstantDetail({
  currentLayer,
  setSelectedTime = () => {}, // Add default fallback
  selectedTime,
  onDataUpdate = null,
  mode = null, // 1: realtime, 2: water
}) {
  // Add validation
  if (typeof setSelectedTime !== "function") {
    console.error("setSelectedTime is not a function:", setSelectedTime);
  }

  const realtime = useSelector(selectUser);
  const infoData = useSelector(selectInfoData);
  const wsData = useSelector(selectWsData);
  const equipmentDetail = useSelector(selectEquipmentDetail);
  const [lastUpdateTime, setLastUpdateTime] = useState("--");
  const [values, setValues] = useState([]);

  // 新增狀態管理
  const [isHistoryMode, setIsHistoryMode] = useState(false);

  const infoList = useMemo(() => {
    if (Array.isArray(infoData)) return infoData;
    if (Array.isArray(infoData?.data)) return infoData.data;
    return [];
  }, [infoData]);

  // 遞迴找到對應層
  function findLayer(equipmentList, targetId) {
    for (const item of equipmentList) {
      if (item.id === targetId) return item;
      if (item.list?.length) {
        const found = findLayer(item.list, targetId);
        if (found) return found;
      }
    }
    return null;
  }

  // 篩選邏輯
  function getFilteredFields(currentLayer, equipmentDetail, realtime) {
    const targetLayer = findLayer(equipmentDetail, currentLayer.id);

    if (!targetLayer) return [];

    const typeIds = targetLayer.field || [];

    return realtime.field.filter((f) => typeIds.includes(f.type_id));
  }

  // 處理時間選擇
  const handleTimeChange = (selectedDateTime) => {
    if (selectedDateTime) {
      const formatted = formatDateTime(selectedDateTime);
      if (formatted) {
        setIsHistoryMode(true);
        setSelectedTime(formatted);
      } else {
        setIsHistoryMode(false);
        if (typeof setSelectedTime === "function") {
          setSelectedTime(null);
        }
      }
    } else {
      // 回到實時模式
      setIsHistoryMode(false);
      if (typeof setSelectedTime === "function") {
        setSelectedTime(null);
      }
    }
  };

  // 歷史模式：設置選時間
  // 實時模式：每次接收到 wsData 更新時間
  useEffect(() => {
    let updateTime;

    if (selectedTime && currentLayer) {
      // 歷史模式且有選擇時間，取 infoData 對應的時間
      updateTime = infoList.find((d) => d.id === currentLayer?.id)?.date_time;
    } else if (wsData && currentLayer) {
      // 即時模式，取 wsData 的時間或 fallback 現在時間
      updateTime =
        wsData.find((d) => d.id === currentLayer?.id)?.date_time ||
        new Date().toISOString();
    }

    setLastUpdateTime(formatDateTime(updateTime));
  }, [isHistoryMode, selectedTime, infoList, currentLayer, wsData]);

  // 計算卡片顯示資料
  useEffect(() => {
    if (!currentLayer || !realtime) {
      return;
    }

    const fieldDefs =
      getFilteredFields(currentLayer, equipmentDetail, realtime) || [];

    if (!fieldDefs || fieldDefs.length === 0) {
      return;
    }

    let sourceData = [];

    if (isHistoryMode) {
      // 歷史模式：使用歷史數據
      const historyLayer = infoList.find(
        (item) => item.id === currentLayer?.id
      );
      sourceData = historyLayer?.data || [];
    } else {
      // 實時模式：使用 WebSocket 完整資料陣列
      const wsDataArray = wsData || []; // wsData 現在是完整陣列
      const layerInfo = infoList.find((item) => item.id === currentLayer?.id);

      // 從 WebSocket 資料陣列中找到對應的 layer
      const wsLayerData = wsDataArray.find(
        (item) => item.id === currentLayer?.id
      );

      sourceData = wsLayerData?.data || layerInfo?.data || [];
    }

    // 最終顯示資料
    const newValues = fieldDefs.map((field) => {
      const dataItem = sourceData?.find((d) => d.type_id === field.type_id);
      return {
        title: field.title || "未知項目",
        value: dataItem ? dataItem.value : "--",
        unit: field.unit || "",
        color: field.color ? `#${field.color}` : "#4CE3E8",
      };
    });

    setValues(newValues);

    if (mode === 1 && onDataUpdate && typeof onDataUpdate === 'function') {
      onDataUpdate({
        sourceData,
        isHistoryMode,
        mode,
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLayer, realtime, infoData, wsData, isHistoryMode, selectedTime]);

  // ========== 渲染區 ==========
  if (!currentLayer) {
    return (
      <div className="flex items-center justify-center min-h-[200px] text-white">
        請選擇設備...
      </div>
    );
  }

  if (values.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[200px] text-white">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto mb-2"></div>
          <div>"資料載入中...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full p-1 text-white">
      <div className="flex items-center justify-end gap-[24px] md:gap-[72px] mb-4 h-[40px]">
        <div className="text-sm flex flex-row md:flex-col items-start justify-center">
          <p className="hidden md:flex">當前資料時間</p>
          <span className="text-sm md:text-lg font-semibold text-end">
            {lastUpdateTime}
          </span>
        </div>
        <div className="relative inline-block">
          <TimeSelector onChange={handleTimeChange} />
          <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none">
            <svg
              className="w-4 h-4 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>
        </div>
      </div>

      {/* 卡片 */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 xxl:grid-cols-6 gap-1 w-full">
        {values.map((item, idx) => {
          return (
            <InstantCard
              key={`${currentLayer.id}-${idx}`}
              title={item.title}
              value={item.value}
              unit={item.unit}
              color={item.color}
            />
          );
        })}
      </div>
    </div>
  );
}
